import { View, Pressable, ActivityIndicator } from "react-native"
import { Alert } from "@src/components/ui/confirm"
import { Link } from "expo-router"
import { trpc } from "utils/trpc"
import { Header } from "components/Header"
import MIcon from "@react-native-vector-icons/material-design-icons"
import { ar } from "i18n/ar"
import { Text } from "components/core"
import { List } from "@src/components/list"
import { useNumCols } from "@src/hooks"
import { useUploadImage } from "@src/hooks/use-product-image-upload"
import { Image } from "expo-image"
import { ENV, sharedStyles } from "@src/utils"

export default function BrandsScreen() {
  const utils = trpc.useUtils()
  const brands = trpc.brands.list.useQuery()
  const deleteBrand = trpc.brands.delete.useMutation()
  const numCols = Math.max(2, useNumCols(250, { maxWidth: 1400 }))
  const _uploadImage = useUploadImage()

  const handleDelete = (id: number) => () => {
    Alert.alert(ar.delete, ar.deleteBrandConfirmation, [
      { text: ar.cancel, style: "cancel" },
      {
        text: ar.delete,
        style: "destructive",
        onPress: async () => {
          await deleteBrand.mutateAsync(
            { id },
            {
              onError() {
                Alert.alert(ar.error, ar.deleteBrandError)
              },
            },
          )
          utils.brands.list.invalidate()
        },
      },
    ])
  }

  const headerRight = (
    <Link href="/dashboard/brand/create" asChild>
      <Pressable className="justify-center px-2 h-full active:opacity-70">
        <MIcon name="plus" size={25} color="blue" />
      </Pressable>
    </Link>
  )

  return (
    <View className="flex-1 bg-gray-100">
      <Header title={ar.brands} right={headerRight} />

      <List
        contentContainerStyle={sharedStyles.contentContainer}
        data={brands.data ?? []}
        numColumns={numCols}
        key={`brands-grid-${numCols}`}
        keyExtractor={(item) => item.id.toString()}
        ListEmptyComponent={brands.isLoading ? <ActivityIndicator className="py-8" /> : <Text className="text-gray-500 text-center py-8">No brands found</Text>}
        renderItem={({ item }) => {
          const image = item.images?.at(-1)

          return (
            <Link href={`/dashboard/brand/${item.id}`} asChild>
              <Pressable className="bg-white mx-1 mb-3 p-5 rounded shadow-sm hover:shadow-xl transition-all border border-gray-100 active:opacity-70 min-h-[80] items-center justify-center">
                <View className="h-10 w-10">
                  {image ? (
                    <Image source={`${ENV.R2_URL}/${image.key}`} style={{ flex: 1 }} contentFit="contain" loading="lazy" transition={250} cachePolicy="memory" />
                  ) : (
                    <MIcon name="tag" size={32} color="#34A85A" />
                  )}
                </View>

                <Text className="text-base font-semibold text-gray-800 text-center mt-2" numberOfLines={2}>
                  {item.name}
                </Text>

                <View className="flex-row">
                  <Link href={`/dashboard/brand/${item.id}/edit`} asChild>
                    <Pressable className="px-4 py-2">
                      <Text className="text-blue-500">{ar.edit}</Text>
                    </Pressable>
                  </Link>
                  <Pressable className="px-4 py-2" onPress={handleDelete(item.id)}>
                    <Text className="text-red-500">{ar.delete}</Text>
                  </Pressable>
                </View>
              </Pressable>
            </Link>
          )
        }}
      />
    </View>
  )
}
